import React, { useEffect, useState } from "react";
import {
  collection,
  getDocs,
  doc,
  updateDoc,
  deleteDoc,
  Timestamp,
  writeBatch
} from "firebase/firestore";
import { MainDb } from "../fireMainStore";

const ProductManager = () => {
  const [products, setProducts] = useState([]);
  const [groups, setGroups] = useState({});
  const [selectedGroupKey, setSelectedGroupKey] = useState("");
  const [selectedSwatch, setSelectedSwatch] = useState("");
  const [selectedSize, setSelectedSize] = useState("");
  const [styleOptions, setStyleOptions] = useState([]);
  const [swatchOptions, setSwatchOptions] = useState([]);
  const [sizeOptions, setSizeOptions] = useState([]);

  const [editingId, setEditingId] = useState(null);
  const [editingData, setEditingData] = useState({});

  // Fetch products
const fetchProducts = async () => {
  const snapshot = await getDocs(collection(MainDb, "items"));
  const items = snapshot.docs.map((d) => ({
    id: d.id, // force correct ID
    ...d.data(),
  }));
  setProducts(items);
};

  useEffect(() => {
    fetchProducts();
  }, []);

  // Group by style + imageId
  const groupProducts = (items) => {
    const grouped = {};
    items.forEach((p) => {
      const key = `${p.style}_${p.imageId}`;
      if (!grouped[key]) grouped[key] = [];
      grouped[key].push(p);
    });
    return grouped;
  };

  useEffect(() => {
    const grouped = groupProducts(products);
    setGroups(grouped);

    // Style dropdown
    const styles = Object.keys(grouped).map((key) => {
      const item = grouped[key][0];
      return { key, label: `${item.style} | ${item.imageId}` };
    });
    setStyleOptions(styles);

    // Swatches + sizes based on selected style
    if (selectedGroupKey && grouped[selectedGroupKey]) {
      const groupItems = grouped[selectedGroupKey];

      setSwatchOptions([
        ...new Set(groupItems.map((p) => p.swatchImage)),
      ]);

      setSizeOptions([
        ...new Set(groupItems.map((p) => p.size)),
      ]);
    } else {
      setSwatchOptions([]);
      setSizeOptions([]);
    }
  }, [products, selectedGroupKey]);

  // Editing logic
  const handleEdit = (product) => {
    const editable = { ...product };
    Object.keys(editable).forEach((k) => {
      if (editable[k] instanceof Timestamp)
        editable[k] = editable[k].toDate().toISOString();
    });
    setEditingId(product.id);
    setEditingData(editable);
  };

  const handleChange = (e, field) => {
    setEditingData((prev) => ({
      ...prev,
      [field]: e.target.value,
    }));
  };
useEffect(() => {
  const ids = products.map(p => p.id);
  const duplicates = ids.filter((id, i) => ids.indexOf(id) !== i);
  if (duplicates.length) {
    console.warn("DUPLICATE IDS:", duplicates);
  }
}, [products]);

  const handleSave = async (id) => {
    const ref = doc(MainDb, "items", id);
    await updateDoc(ref, editingData);
    setEditingId(null);
    fetchProducts();
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this?")) return;
    await deleteDoc(doc(MainDb, "items", id));
    fetchProducts();
  };
const handleDeleteColorGroup = async (groupItems, swatch) => {
  if (!window.confirm("Delete this color for this style/imageId?")) return;

  try {
    const toDelete = groupItems.filter(
      (item) =>
        item.swatchImage === swatch
    );

    for (const item of toDelete) {
      await deleteDoc(doc(MainDb, "items", item.id));
    }

    fetchProducts();
  } catch (error) {
    console.error("Error deleting color group:", error);
  }
};
  // Filtered data
  const displayedGroups = selectedGroupKey
    ? {
        [selectedGroupKey]: groups[selectedGroupKey]?.filter((p) => {
          return (
            (!selectedSwatch || p.swatchImage === selectedSwatch) &&
            (!selectedSize || p.size === selectedSize)
          );
        }),
      }
    : groups;
// const downloadCSV = () => {
//   if (!products.length) return;

//   const headers = Object.keys(products[0]);

//   const rows = products.map((item) =>
//     headers.map((field) => {
//       let value = item[field];

//       if (Array.isArray(value)) value = value.join("|");
//       if (value?.seconds) value = new Date(value.seconds * 1000).toISOString();

//       return `"${(value ?? "").toString().replace(/"/g, '""')}"`;
//     })
//   );

//   const csvContent =
//     [headers.join(","), ...rows.map((r) => r.join(","))].join("\n");

//   const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
//   const url = URL.createObjectURL(blob);

//   const link = document.createElement("a");
//   link.href = url;
//   link.download = "products_export.csv";
//   link.click();
// };
const downloadCSV = () => {
  if (!products.length) return;

  // Map old field names -> new CSV column names
  const columnMap = {
    id: "id",
    backImage: "backImage",
    saleStart: "SALE_START_DATE",
    price: "Price",
    saleEnd: "endSale",
    // pieceCost: "piece_cost",
    grams: "VariantGrams",
    title: "Title",
    imageAltText: "ImageAltText",
    availableSizes: "AVAILABLE_SIZES",
    salePrice: "salePrice",
    frontImage: "frontImage",
    measurements: "measure_asli",
    tags: "Tags",
    description: "Body",
    pieceCost: "PieceCost",
    variantImage: "VariantImage",
    side: "side",
    color: "Color",
    PRODUCT_MEASUREMENTS: "PRODUCT_MEASUREMENTS",
    style: "Style",
    colorApiName: "colorName4API",
    specSheet: "DECORATION_SPEC_SHEET",
    size: "Size",
    swatchImage: "ImageCircle",
    imageId: "ImageID",
    mainPath: "mainPath",
    status: "Status",
    type: "Type",
    createdAt: "createdAt",
    source: "source",
    variantId: "variantId",
    vendorSku: "vendorSku",
  };

  // Only use mapped columns
  const headers = Object.keys(columnMap);

  // Final CSV header names
  const csvHeaders = headers.map((key) => columnMap[key]);

  const rows = products.map((item) =>
    headers.map((field) => {
      let value = item[field];

      if (Array.isArray(value)) value = value.join("|");

      if (value?.seconds) {
        value = new Date(value.seconds * 1000).toISOString();
      }

      return `"${(value ?? "").toString().replace(/"/g, '""')}"`;
    })
  );

  const csvContent = [
    csvHeaders.join(","),
    ...rows.map((r) => r.join(",")),
  ].join("\n");

  const blob = new Blob([csvContent], {
    type: "text/csv;charset=utf-8;",
  });

  const url = URL.createObjectURL(blob);

  const link = document.createElement("a");
  link.href = url;
  link.download = "products_export.csv";
  link.click();

  URL.revokeObjectURL(url);
};
const handleSaveGroup = async (product) => {
  const batch = writeBatch(MainDb);

  const groupItems = products.filter(
    (p) =>
      p.style === product.style &&
      p.imageId === product.imageId
  );

  // REMOVE ID from payload
  const { id, ...safeData } = editingData;

  groupItems.forEach((item) => {
    const ref = doc(MainDb, "items", item.id);
    batch.update(ref, safeData);
  });

  await batch.commit();

  setEditingId(null);
  fetchProducts();
};
  return (
    <div style={{ padding: 20 }}>
    
<div
  style={{
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: 15,
  }}
>
  <h2>Product Manager</h2>

  <button
    onClick={downloadCSV}
    style={{
      padding: "8px 14px",
      background: "#0b74de",
      color: "white",
      border: "none",
      borderRadius: 6,
      cursor: "pointer",
      fontWeight: "bold",
    }}
  >
    Download CSV
  </button>
</div>
      {/* STYLE DROPDOWN */}
      <select
        value={selectedGroupKey}
        onChange={(e) => {
          setSelectedGroupKey(e.target.value);
          setSelectedSwatch("");
          setSelectedSize("");
        }}
      >
        <option value="">All Styles</option>
        {styleOptions.map((s) => (
          <option key={s.key} value={s.key}>
            {s.label}
          </option>
        ))}
      </select>

      <div
  style={{
    marginTop: 15,
    display: "flex",
    gap: 10,
    flexWrap: "wrap",
    alignItems: "center"
  }}
>
  {swatchOptions.map((swatch) => (
    <div key={swatch} style={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
      <img
        src={swatch}
        alt="swatch"
        onClick={() => setSelectedSwatch(swatch)}
        style={{
          width: 40,
          height: 40,
          cursor: "pointer",
          border:
            selectedSwatch === swatch
              ? "3px solid blue"
              : "1px solid gray",
          borderRadius: 4,
        }}
      />

      {selectedGroupKey && (
        <button
          onClick={() =>
            handleDeleteColorGroup(groups[selectedGroupKey], swatch)
          }
          style={{
            marginTop: 4,
            fontSize: 10,
            background: "red",
            color: "white",
            border: "none",
            borderRadius: 3,
            cursor: "pointer"
          }}
        >
          delete
        </button>
      )}
    </div>
  ))}
</div>

      {/* SIZE DROPDOWN */}
      {sizeOptions.length > 0 && (
        <select
          value={selectedSize}
          onChange={(e) => setSelectedSize(e.target.value)}
          style={{ marginTop: 10 }}
        >
          <option value="">All Sizes</option>
          {sizeOptions.map((size) => (
            <option key={size} value={size}>
              {size}
            </option>
          ))}
        </select>
      )}

      {/* PRODUCTS */}
      {Object.entries(displayedGroups).map(([key, group]) => {
        if (!group || group.length === 0) return null;
        const first = group[0];

        return (
          <div key={key} style={{ border: "1px solid #ccc", marginTop: 20, padding: 10 }}>
            <h3>
              Style: {first.style} | ImageId: {first.imageId}
            </h3>
{/* 

            {/* {group.map((p) => ( */}
            {Object.values(
  group.reduce((acc, item) => {
    const colorKey = item.swatchImage || "noColor";
    if (!acc[colorKey]) acc[colorKey] = item;
    return acc;
  }, {})
).map((p) => (
  <div
   key={`${key}_${p.id}`}
    style={{
      borderTop: "1px dashed #aaa",
      marginTop: 10,
      paddingTop: 10,
    }}
  >
    {/* ALWAYS VISIBLE ROW */}
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: 15,
        flexWrap: "wrap",
      }}
    >
      {/* FRONT IMAGE */}
      {p.frontImage && (
        <img
          src={p.frontImage}
          alt=""
          style={{
            width: 180,
            height: 180,
            objectFit: "contain",
            borderRadius: 4,
            border: "1px solid #ccc",
          }}
        />
      )}

      {/* BUTTONS */}
      {editingId === p.id ? (
        <>
        <button onClick={() => handleSave(p.id)}>Save Variant</button>
<button onClick={() => handleSaveGroup(p)}>Save All Sizes</button>
          {/* <button onClick={() => handleSaveGroup(p)}>Save</button> */}
          <button onClick={() => setEditingId(null)}>Cancel</button>
        </>
      ) : (
        <>
          <button onClick={() => handleEdit(p)}>Edit</button>
          <button onClick={() => handleDelete(p.id)}>Delete</button>
        </>
      )}
    </div>

    {/* EDITING PANEL (ONLY WHEN EDITING) */}
    {editingId === p.id && (
      <div style={{ marginTop: 10 }}>
        {Object.keys(p).map((field) => (
          <div key={field} style={{ marginBottom: 5 }}>
            <b>{field}:</b>{" "}
<input
  value={
    Array.isArray(editingData[field])
      ? editingData[field].join(", ")
      : editingData[field] || ""
  }
  onChange={(e) => handleChange(e, field)}
  style={{ width: "100%" }}
/>
          </div>
        ))}
      </div>
    )}
  </div>
))}
          </div>
        );
      })}
    </div>
  );
};

export default ProductManager;